import React, {
    useEffect,
    useState,
    forwardRef,
    useImperativeHandle,
} from "react";
import TableContainer from "Common/TableContainer";
import { useFormik } from "formik";
import { X } from "lucide-react";
import "flatpickr/dist/flatpickr.css";
import Select from "react-select";
import * as Yup from "yup";
import {
    getCountries,
    getDepartmentsByCountry,
    getMunicipalitiesByDepartment,
    getAddressesByCustomerId,
    addAddressList,
    getTypeAddressList,
} from "helpers/fakebackend_helper";

const AddressForm = forwardRef(({ onSuccess, customerId }: { onSuccess?: () => void; customerId: number | null; }, ref) => {

    const [countries, setCountries] = useState<
        { value: string; label: string }[]
    >([]);
    const [departments, setDepartments] = useState<
        { value: string; label: string }[]
    >([]);
    const [municipalities, setMunicipalities] = useState<
        { value: string; label: string }[]
    >([]);
    const [typeAddress, setTypeAddress] = useState<
        { value: string; label: string }[]
    >([]);
    const [showAddressForm, setShowAddressForm] = useState(false);
    const [addressList, setAddressList] = useState<any[]>([]);

    const linkifyEntity = (entityName: string, id: number) => {
        return `/${entityName}/${id}`;
    };

    const handleAddNewAddress =  () => {
        validation.resetForm();
        setShowAddressForm(true);
    };

    const saveNewAddress = async () => {
        await validation.handleSubmit();
    };

    const handleBackToList = () => {
        setShowAddressForm(false);
    };

    useImperativeHandle(ref, () => ({
        submitForm: () => validation.handleSubmit(),
        resetForm: () => validation.resetForm(),
        hasAddresses: () => addressList.length > 0,
    }));

    const validation: any = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: {
            typeAddress: "",
            municipality: "",
            department: "",
            country: "",
            sector: "",
            street: "",
            number: "",
            reference: "",
        },

        validationSchema: Yup.object({
            typeAddress: Yup.string().required("Tipo de dirección es requerida"),
            municipality: Yup.string().required("Municipio es requerido"),
            department: Yup.string().required("Departamento es requerido"),
            country: Yup.string().required("País es requerido"),
            sector: Yup.string().required("Sector es requerido"),
            street: Yup.string().required("Calle es requerida"),
            number: Yup.string().required("Número es requerido"),
            reference: Yup.string().required("Referencia es requerida"),
        }),

        onSubmit: async (values: any) => {
            if (!customerId) {
                console.error("No se puede enviar dirección sin customerId.");
                return;
            }

            try {
                const addressData = {
                    typeAddress: linkifyEntity("typeAddresses", values.typeAddress),
                    municipality: linkifyEntity("municipalities", values.municipality),
                    department: linkifyEntity("departments", values.department),
                    country: linkifyEntity("countries", values.country),
                    customer: linkifyEntity("customers", customerId),
                    sector: values.sector,
                    street: values.street,
                    number: values.number,
                    reference: values.reference,
                };
                const response = await addAddressList(addressData);
                console.log("Address created successfully:", response);

                setAddressList(prev => [...prev, { ...values }]);

                setShowAddressForm(false);
                validation.resetForm();

                onSuccess?.();
            } catch (error) {
                console.error("Error creating address:", error);
            }
        },
    });

    const columnsAddress = [
        {
            header: "Tipo Dirección",
            accessorKey: "typeAddress",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Municipio",
            accessorKey: "municipality",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Departamento",
            accessorKey: "department",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "País",
            accessorKey: "country",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Sector",
            accessorKey: "sector",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Calle",
            accessorKey: "street",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Número",
            accessorKey: "number",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Referencia",
            accessorKey: "reference",
            enableColumnFilter: false,
            enableSorting: true,
        },
    ];

    const { country, department } = validation.values;
    const setFieldValue = validation.setFieldValue;


    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await getCountries();
                const data = response.data?._embedded?.countries || [];
                const options = data.map((item: any) => ({
                    value: item.countryId,
                    label: item.name,
                }));
                setCountries(options);
            } catch (error) {
                console.error("Error loading countries:", error);
            }
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        if (!country) return;

        const fetchDepartments = async () => {
            try {
                const response = await getDepartmentsByCountry(country);
                const data = response.data?._embedded?.departments || [];
                const options = data.map((item: any) => ({
                    value: item.departmentId,
                    label: item.name,
                }));
                setDepartments(options);
                setMunicipalities([]);
                setFieldValue("department", "");
                setFieldValue("municipality", "");
            } catch (error) {
                console.error("Error loading departments:", error);
            }
        };

        fetchDepartments();
    }, [country, setFieldValue]);

    useEffect(() => {
        if (!department) return;

        const fetchMunicipalities = async () => {
            try {
                const response = await getMunicipalitiesByDepartment(department);
                const data = response.data?._embedded?.municipalities || [];
                const options = data.map((item: any) => ({
                    value: item.municipalityId,
                    label: item.name,
                }));
                setMunicipalities(options);
                setFieldValue("municipality", "");
            } catch (error) {
                console.error("Error loading municipalities:", error);
            }
        };

        fetchMunicipalities();
    }, [department, setFieldValue]);

    useEffect(() => {
        const fetchTypeAddress = async () => {
            try {
                const response = await getTypeAddressList();
                const embedded = response.data?._embedded?.typeAddresses;
                const options = embedded
                    ? Object.values(embedded).map((typeAddress: any) => ({
                        value: typeAddress.typeAddressId,
                        label: typeAddress.name,
                    }))
                    : [];
                setTypeAddress(options);
            } catch (error) {
                console.error("Error fetching type customers:", error);
            }
        };
        fetchTypeAddress();
    }, []);

    useEffect(() => {
        if (!customerId) return;

        const fetchAddressesByCustomerId = async () => {
            try {
                const response = await getAddressesByCustomerId(customerId.toString());
                const rawList = response.data?._embedded?.addresses || [];

                const addressList = rawList.map((addr: any) => ({
                    ...addr,
                    typeAddress: addr._embedded?.typeAddress?.name || '',
                    municipality: addr._embedded?.municipality?.name || '',
                    department: addr._embedded?.municipality?.department?.name || '',
                    country: addr._embedded?.municipality?.department?.country?.name || '',
                }));
                console.log(addAddressList, 'addressList');
                setAddressList(addressList);
            } catch (error) {
                console.error("Error fetching type address:", error);
            }
        };
        fetchAddressesByCustomerId();
    }, []);

    return (
        <React.Fragment>
            {showAddressForm ? (
                <>
                    <h5 className="mb-3">Dirección</h5>
                    <form onSubmit={validation.handleSubmit}>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label
                                    htmlFor="typeAddressSelect"
                                    className="inline-block mb-2 text-base font-medium"
                                >
                                    Tipo de Dirección<span className="text-red-500">*</span>
                                </label>
                                <Select
                                    className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                    data-choices
                                    name="typeAddress"
                                    options={typeAddress}
                                    value={typeAddress.find(
                                        option => option.value === validation.values.typeAddress
                                    )}
                                    onChange={(selectedOption: any) => {
                                        validation.setFieldValue("typeAddress", selectedOption.value);
                                    }}
                                    onBlur={validation.handleBlur}
                                />
                                {validation.touched.typeAddress &&
                                    validation.errors.typeAddress ? (
                                    <div className="text-red-500">
                                        {validation.errors.typeAddress}
                                    </div>
                                ) : null}
                            </div>

                            <div>
                                <label
                                    htmlFor="countrySelect"
                                    className="inline-block mb-2 text-base font-medium"
                                >
                                    País<span className="text-red-500">*</span>
                                </label>
                                <Select
                                    className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                    data-choices
                                    name="country"
                                    options={countries}
                                    value={countries.find(
                                        option => option.value === validation.values.country
                                    )}
                                    onChange={(selectedOption: any) => {
                                        validation.setFieldValue("country", selectedOption.value);
                                    }}
                                    onBlur={validation.handleBlur}
                                />
                                {validation.touched.country &&
                                    validation.errors.country ? (
                                    <div className="text-red-500">
                                        {validation.errors.country}
                                    </div>
                                ) : null}
                            </div>

                            <div>
                                <label
                                    htmlFor="departmentSelect"
                                    className="inline-block mb-2 text-base font-medium"
                                >
                                    Departamento<span className="text-red-500">*</span>
                                </label>
                                <Select
                                    className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                    data-choices
                                    name="department"
                                    options={departments}
                                    value={departments.find(
                                        option => option.value === validation.values.department
                                    )}
                                    onChange={(selectedOption: any) => {
                                        validation.setFieldValue("department", selectedOption.value);
                                    }}
                                    onBlur={validation.handleBlur}
                                />
                                {validation.touched.department &&
                                    validation.errors.department ? (
                                    <div className="text-red-500">
                                        {validation.errors.department}
                                    </div>
                                ) : null}
                            </div>

                            <div>
                                <label
                                    htmlFor="municipalitySelect"
                                    className="inline-block mb-2 text-base font-medium"
                                >
                                    Municipio<span className="text-red-500">*</span>
                                </label>
                                <Select
                                    className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                    data-choices
                                    name="municipality"
                                    options={municipalities}
                                    value={municipalities.find(
                                        option => option.value === validation.values.municipality
                                    )}
                                    onChange={(selectedOption: any) => {
                                        validation.setFieldValue("municipality", selectedOption.value);
                                    }}
                                    onBlur={validation.handleBlur}
                                />
                                {validation.touched.municipality &&
                                    validation.errors.municipality ? (
                                    <div className="text-red-500">
                                        {validation.errors.municipality}
                                    </div>
                                ) : null}
                            </div>

                            <div>
                                <label
                                    htmlFor="sectorInput"
                                    className="inline-block mb-2 text-base font-medium"
                                >
                                    Sector <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="sector"
                                    className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                    placeholder="Ingrese sector"
                                    value={validation.values.sector}
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                />
                                {validation.touched.sector && validation.errors.sector ? (
                                    <div className="text-red-500">{validation.errors.sector}</div>
                                ) : null}
                            </div>

                            <div>
                                <label
                                    htmlFor="streetInput"
                                    className="inline-block mb-2 text-base font-medium"
                                >
                                    Calle <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="street"
                                    className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                    placeholder="Ingrese calle"
                                    value={validation.values.street}
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                />
                                {validation.touched.street && validation.errors.street ? (
                                    <div className="text-red-500">{validation.errors.street}</div>
                                ) : null}
                            </div>

                            <div>
                                <label
                                    htmlFor="numberInput"
                                    className="inline-block mb-2 text-base font-medium"
                                >
                                    Número <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    name="number"
                                    className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                    placeholder="Ingrese número"
                                    value={validation.values.number}
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                />
                                {validation.touched.number && validation.errors.number ? (
                                    <div className="text-red-500">{validation.errors.number}</div>
                                ) : null}
                            </div>

                            <div>
                                <label
                                    htmlFor="referenceInput"
                                    className="inline-block mb-2 text-base font-medium"
                                >
                                    Referencia <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="reference"
                                    className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                    placeholder="Ingrese referencia"
                                    value={validation.values.reference}
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                />
                                {validation.touched.reference && validation.errors.reference ? (
                                    <div className="text-red-500">{validation.errors.reference}</div>
                                ) : null}
                            </div>
                        </div>
                    </form>
                    <div className="flex justify-end gap-2 mt-6">
                        <button type="button" onClick={handleBackToList} className="text-red-500 bg-rose-200 btn hover:text-red-500 hover:bg-rose-300 focus:text-red-500 focus:bg-rose-300 active:text-red-500 active:bg-rose-300 dark:bg-zink-700 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10"><X className="inline-block size-4" /> <span className="align-middle">Descartar</span></button>
                        <button
                            type="submit"
                            onClick={saveNewAddress}
                            className="text-white btn bg-slate-400 border-slate-400 hover:text-white hover:bg-slate-600 hover:border-slate-600 focus:text-white focus:bg-slate-600 focus:border-slate-600 focus:ring focus:ring-slate-100 active:text-white active:bg-slate-600 active:border-slate-600 active:ring active:ring-slate-100 dark:ring-slate-400/20"
                        >
                            Guardar Dirección
                        </button>

                    </div>

                </>
            ) : (
                // Listado de direcciones
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-semibold">Listado de Direcciones</h2>
                        <button
                            type="button"
                            className="text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100" id="addRow"
                            onClick={() => validation.handleSubmit()}
                        >
                            Agregar Dirección
                        </button>
                    </div>
                    {addressList.length === 0 ? (
                        <p className="text-gray-500">No se han agregado direcciones aún.</p>
                    ) : (
                        <div className="card-body">
                            <div className="mb-3">
                            </div>
                            <TableContainer
                                isPagination={true}
                                isSelect={true}
                                isGlobalFilter={true}
                                columns={columnsAddress}
                                data={addressList}
                                customPageSize={5}
                                divclassName="my-2 col-span-12 overflow-x-auto lg:col-span-12"
                                tableclassName="hover group dataTable w-full text-sm align-middle whitespace-nowrap no-footer"
                                theadclassName="border-b border-slate-200 dark:border-zink-500"
                                trclassName="group-[.stripe]:even:bg-slate-50 group-[.stripe]:dark:even:bg-zink-600 transition-all duration-150 ease-linear group-[.hover]:hover:bg-slate-50 dark:group-[.hover]:hover:bg-zink-600 [&.selected]:bg-custom-500 dark:[&.selected]:bg-custom-500 [&.selected]:text-custom-50 dark:[&.selected]:text-custom-50"
                                thclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500 sorting px-3 py-4 text-slate-900 bg-slate-200/50 font-semibold text-left dark:text-zink-50 dark:bg-zink-600 dark:group-[.bordered]:border-zink-500"
                                tdclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500"
                                PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
                            />
                        </div>
                    )}

                </div>
            )}
        </React.Fragment>
    );
});

export default AddressForm;
